import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { Cta } from '../../cta';
import type { LinkProps } from '../../../atoms/types';
import Pro from '../../../icons/pro';
import { DetailedStickerType, MainCtaInfos } from '../../types';
import { MarkStars } from '../../mark-with-stars';
import { DatasheetAttribute } from '../service/types';
import { MaterialSymbol } from '../../../atoms/material-symbol';
import DetailedSticker from '../../../icons/sticker/detailedSticker';

export type LargeCardProps = {
  id?: number;
  title?: string;
  rating?: number;
  pros?: any;
  image: { imageId: string; alt: string; extension?: string; title?: string };
  mainCtaInfos?: MainCtaInfos;
  testLink?: LinkProps;
  topDataSheet?: DatasheetAttribute[];
  urlProduct?: string;
  detailedSticker?: DetailedStickerType;
};

const Wrapper = styled.div`
  background: var(--color-alt-4);
  border-radius: 10px;
  box-shadow: 0px 0px 30px 0px var(--color-box-shadow);
  margin: var(--spacer-fluid) 0;
  padding: var(--spacer-fluid) var(--spacer-m-to-s);
  display: flex;
  flex-direction: column;
  gap: var(--spacer);
  position: relative;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(--spacer-fluid);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
    `,
  )}
`;

const LeftSide = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      align-items: stretch;
      margin: initial;
      width: auto;
    `,
  )}
`;

const MiddleSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: auto;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin-right: auto;
    `,
  )}
`;
const Title = styled.div`
  font-family: var(--theme-typography-primary-font);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.38;
  text-align: center;

  a {
    color: var(--theme-color-title);
    text-decoration: underline;
    transition: color 0.3s;

    &:hover {
      color: var(--theme-color-primary);
    }
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      text-align: initial;
    `,
  )}
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  gap: 12px;
  list-style: none;

  svg {
    min-width: 22px;
  }
`;

const Review = styled.div`
  color: var(--text-color-alt);
  font-family: var(--theme-typography-primary-font);
  font-weight: bold;
  font-size: 18px;
  text-align: center;
  text-decoration: underline;
  transition: color 0.3s;

  &:hover {
    color: var(--theme-color-primary);
  }
`;

const ProItem = styled.li`
  align-items: start;
  display: flex;
  gap: 7px;
  line-height: 22px;
`;

const RefurbishedCtaContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const RefurbishedLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 10px;
  gap: 3px;
`;

const RefurbishedLabel = styled.div`
  font-size: 12px;
  line-height: 12px;
`;

const ShopIcon = styled.img`
  height: 24px;
  width: 24px;
`;

const WaitingShopIcon = styled.div`
  height: 24px;
  width: 24px;
`;

const Specs = styled.ul`
  list-style-type: none;
`;

const Spec = styled.li`
  font-size: 16px;
  line-height: 18px;
  margin: 0 0 10px;
  padding-left: 25px;
  position: relative;

  &:last-of-type {
    margin: 0;
  }
`;

const SpecIcon = styled.span`
  font-size: 18px;
  left: 0;
  margin: 0 12px 0 0;
  position: absolute;
  top: -1p;
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: auto;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      position: relative;
      margin-right: auto;
    `,
  )}
`;

const RightSideInformations = styled.div`
  display: flex;
  gap: var(--spacer-default);
`;

const AwardContainer = styled.div`
  position: relative;
  z-index: 9;
  padding: initial;
  margin: auto;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      position: absolute;
      right: var(--spacer-s);
      top: calc(-0.8 * var(--spacer-m));
    `,
  )}
`;

const getDefaultLabel = (
  layout: string,
  sponsored: boolean,
  internalLink: boolean,
  defaultLabels: { [key: string]: string },
): string => {
  const correspondingKey = Object.keys(defaultLabels).find((objectKey) => {
    return objectKey === layout;
  });

  if (correspondingKey) {
    if (!internalLink && sponsored) {
      return defaultLabels.external;
    }

    return defaultLabels[correspondingKey];
  }

  if (internalLink) {
    return defaultLabels.default;
  }

  return defaultLabels.external;
};

const CtaLargeCard = ({
  layout,
  internalLink,
  sponsored,
  shopName,
  icon,
  label,
  refurbished,
  ctaPropsCard,
}: MainCtaInfos) => {
  const [lazyShopLogo, setLazyShopLogo] = useState(false);
  const t = useTranslation();
  const defaultLabels = {
    default: t('design.largeCard.defaultLabel.default'),
    download: t('design.largeCard.defaultLabel.download'),
    external: t('design.largeCard.defaultLabel.external'),
  };
  const defaultLabel = getDefaultLabel(layout, sponsored, internalLink, defaultLabels);

  useEffect(() => {
    if (!lazyShopLogo) {
      setLazyShopLogo(true);
    }
  }, [lazyShopLogo, setLazyShopLogo]);

  if (layout === 'product' && !label) {
    return null;
  }

  if (refurbished) {
    return (
      <Cta {...ctaPropsCard}>
        <RefurbishedCtaContainer>
          {icon && lazyShopLogo && <ShopIcon src={icon} alt={shopName || ''} />}
          {icon && !lazyShopLogo && <WaitingShopIcon />}
          <RefurbishedLabelContainer>
            {label || defaultLabel}
            <RefurbishedLabel>{t('design.refurbished')}</RefurbishedLabel>
          </RefurbishedLabelContainer>
        </RefurbishedCtaContainer>
      </Cta>
    );
  }

  return (
    <Cta {...ctaPropsCard}>
      {icon && lazyShopLogo && <ShopIcon src={icon} alt={shopName || ''} />}
      {icon && !lazyShopLogo && <WaitingShopIcon />}
      {label || defaultLabel}
    </Cta>
  );
};

const LargeCard = ({
  title,
  rating,
  pros,
  image,
  mainCtaInfos,
  testLink,
  topDataSheet,
  urlProduct,
  detailedSticker,
}: LargeCardProps) => {
  const t = useTranslation();

  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  const imageProps = {
    ...image,
    mobileWidth: 140,
    mobileHeight: 140,
    desktopWidth: 180,
    desktopHeight: 180,
  };

  const rightInfomations = useMemo(
    () =>
      mainCtaInfos?.layout === 'online-service' ? (
        <Specs>
          {topDataSheet?.map((attribute: DatasheetAttribute) => (
            <Spec className="marty-service-card__specifications-item" key={attribute.name}>
              <MaterialSymbol
                className="material-symbols-outlined"
                symbol={attribute.iconSlug}
                Component={SpecIcon}
                lazy={false}
              />
              {attribute.type === 'numeric' ? `${attribute.value} ${attribute.name.toLowerCase()}` : attribute.value}
            </Spec>
          ))}
        </Specs>
      ) : (
        <List>
          {pros?.slice(0, 3)?.map((pro: string) => (
            <ProItem key={pro}>
              <Pro height={22} width={22} color="var(--theme-palette-green)" />
              {pro}
            </ProItem>
          ))}
        </List>
      ),
    [pros, topDataSheet, mainCtaInfos?.layout],
  );

  return (
    <Wrapper>
      <Header>
        <LeftSide>
          <ImageComponent {...imageProps} />
        </LeftSide>
        <MiddleSide>
          {mainCtaInfos.link ? (
            <Title>
              <LinkComponent href={urlProduct} title={title}>{`${title}`}</LinkComponent>
            </Title>
          ) : (
            <Title>{`${title}`}</Title>
          )}
          <RightSideInformations>
            {/* {!pros?.length && <>{t('de  sign.largeCard.nothingToDisplay')}</>} */}
            {rightInfomations}
          </RightSideInformations>
        </MiddleSide>
        <RightSide>
          {detailedSticker && (
            <AwardContainer>
              <DetailedSticker detailedSticker={detailedSticker} scale={0.17} />
            </AwardContainer>
          )}
          {rating > 0 && <MarkStars className="mod-dark" rating={rating} max={10} />}
        </RightSide>
      </Header>
      {mainCtaInfos.link && <CtaLargeCard {...mainCtaInfos} />}
      {testLink && testLink?.href && (
        <Review>
          <LinkComponent {...testLink}>{t('design.miniCard.test')}</LinkComponent>
        </Review>
      )}
    </Wrapper>
  );
};

export default LargeCard;
